import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { 
  TrendingUp, 
  Users, 
  Eye, 
  MousePointer, 
  Clock,
  FileText,
  BarChart3,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Analytics = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7days'); // 7days, 30days, 90days

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/analytics?range=${timeRange}`,
        getAuthHeaders()
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const overviewStats = [
    {
      icon: Eye,
      title: 'Total Page Views',
      value: analytics?.total_page_views || 0,
      change: analytics?.page_views_change || 0,
      color: 'bg-blue-500',
      description: `Last ${timeRange}`
    },
    {
      icon: Users,
      title: 'Unique Visitors',
      value: analytics?.unique_visitors || 0,
      change: analytics?.visitors_change || 0,
      color: 'bg-green-500',
      description: `Last ${timeRange}`
    },
    {
      icon: Clock,
      title: 'Avg. Session Duration',
      value: analytics?.avg_session_duration || '0m 0s',
      change: analytics?.duration_change || 0,
      color: 'bg-purple-500',
      description: 'Time on site'
    },
    {
      icon: MousePointer,
      title: 'Bounce Rate',
      value: `${analytics?.bounce_rate || 0}%`,
      change: analytics?.bounce_change || 0,
      color: 'bg-orange-500',
      description: 'Single page visits',
      inverse: true // Lower is better
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Website traffic and user engagement insights</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTimeRange('7days')}
            className={`px-4 py-2 rounded-lg ${
              timeRange === '7days'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 7 Days
          </button>
          <button
            onClick={() => setTimeRange('30days')}
            className={`px-4 py-2 rounded-lg ${
              timeRange === '30days'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setTimeRange('90days')}
            className={`px-4 py-2 rounded-lg ${
              timeRange === '90days'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 90 Days
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {overviewStats.map((stat, index) => {
          const Icon = stat.icon;
          const isPositive = stat.inverse ? stat.change < 0 : stat.change > 0;
          const changeColor = isPositive ? 'text-green-600' : 'text-red-600';
          
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {stat.change !== 0 && (
                    <div className={`flex items-center ${changeColor} text-sm font-semibold`}>
                      <TrendingUp className={`w-4 h-4 mr-1 ${!isPositive && 'rotate-180'}`} />
                      {Math.abs(stat.change)}%
                    </div>
                  )}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">{stat.title}</p>
                <p className="text-xs text-gray-500">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Articles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5" />
              <span>Top Performing Articles</span>
            </CardTitle>
            <CardDescription>Most viewed articles in the selected period</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.top_articles && analytics.top_articles.length > 0 ? (
              <div className="space-y-4">
                {analytics.top_articles.map((article, index) => (
                  <div key={article.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm line-clamp-1">{article.title_bengali}</p>
                      <div className="flex items-center space-x-3 mt-1">
                        <div className="flex items-center text-gray-500">
                          <Eye className="w-3 h-3 mr-1" />
                          <span className="text-xs">{article.views} views</span>
                        </div>
                        <span className="text-xs text-gray-400">{article.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No data available</p>
            )}
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span>Traffic Sources</span>
            </CardTitle>
            <CardDescription>Where your visitors come from</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.traffic_sources && analytics.traffic_sources.length > 0 ? (
              <div className="space-y-4">
                {analytics.traffic_sources.map((source, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">{source.name}</span>
                      <span className="text-sm text-gray-600">{source.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-red-600 h-2 rounded-full"
                        style={{ width: `${source.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">{source.visitors} visitors</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Direct</span>
                    <span className="text-sm text-gray-600">45%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '45%' }} />
                  </div>
                  <p className="text-xs text-gray-500">Direct visits</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Social Media</span>
                    <span className="text-sm text-gray-600">30%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '30%' }} />
                  </div>
                  <p className="text-xs text-gray-500">From social platforms</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Search Engines</span>
                    <span className="text-sm text-gray-600">25%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '25%' }} />
                  </div>
                  <p className="text-xs text-gray-500">Organic search traffic</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Content Engagement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Content Engagement by Location</span>
          </CardTitle>
          <CardDescription>Which locations generate the most engagement</CardDescription>
        </CardHeader>
        <CardContent>
          {analytics?.location_stats && analytics.location_stats.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {analytics.location_stats.map((location, index) => (
                <div key={index} className="p-4 border rounded-lg hover:border-red-600 transition-colors">
                  <h4 className="font-bold text-lg">{location.name}</h4>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Articles</span>
                      <span className="font-semibold">{location.articles}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Views</span>
                      <span className="font-semibold">{location.views}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg. Views</span>
                      <span className="font-semibold">{location.avg_views}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No location data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
